import "./App.css";
import { useTranslation } from "react-i18next";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import AuthStatus from "./auth/AuthStatus";
import { AuthProvider } from "./auth/AuthContext";
import { useAuthContext } from "./auth/AuthContext";
import LoginScreen from "./auth/LoginScreen";
import PricingTable from "./components/pricing-table/pricing-table";
import LanguageSwitcher from "./components/LanguageSwitcher";
import OrderPage from "./pages/order/OrderPage";
import CancellationConfirmPage from "./pages/cancellation/CancellationConfirmPage";
import CancellationPage from "./pages/cancellation/CancellationPage";
import DsaReportPage from "./pages/dsa/DsaReportPage";
import WithdrawalConfirmPage from "./pages/withdrawal/WithdrawalConfirmPage";
import WithdrawalPage from "./pages/withdrawal/WithdrawalPage";
import LegalLinks from "./components/legal/LegalLinks";
import { withEntryQuery } from "./entryParams";
import {
  CANCELLATION_CONFIRM_PATH,
  CANCELLATION_PATH,
  DSA_PATH,
  WITHDRAWAL_CONFIRM_PATH,
  WITHDRAWAL_PATH,
} from "./routes";
import "./auth/LoginScreen.css";

/** Chrome shared by every route: app bar, panel, footer. */
function AppLayout() {
  const { t } = useTranslation();

  return (
    <div className="layout">
      <header className="app-bar">
        <div className="brand">
          <img src="/topbar_logo.svg" alt="Lyra Logo" className="brand-logo" />
        </div>
        <h1 className="page-title">{t("app.title")}</h1>
        <div className="app-bar-right">
          <LanguageSwitcher />
          <AuthStatus />
        </div>
      </header>

      <main>
        <div className="content-shell">
          <section className="payment-panel" aria-label="Lyra Bezahlbereich">
            <Outlet />
          </section>
        </div>
      </main>

      <footer className="site-footer">
        <div className="footer-inner">
          <div className="footer-top">
            <div className="footer-column">
              <img
                src="/topbar_logo.svg"
                alt="Lyra Logo"
                className="footer-logo"
              />
              <p className="footer-tagline">{t("app.footer.tagline")}</p>
            </div>

            <div className="footer-column footer-right">
              <p>
                {t("app.footer.builtBy")}{" "}
                <a
                  href="https://eternalfoo.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-eternal"
                >
                  eternalfoo
                </a>
              </p>
            </div>
          </div>

          <div className="footer-bottom">
            <p>{t("app.footer.rights")}</p>

            {/*
              The cancellation, the withdrawal and the DSA notice must be
              reachable without a login (§ 312k Abs. 2 BGB, § 356a BGB, Art. 16
              DSA), so they hang in the footer
              of every page. ⚠️ The same nav is rendered on the login screen,
              which has no footer of its own - see LegalLinks.
            */}
            <LegalLinks />
          </div>
        </div>
      </footer>
    </div>
  );
}

/**
 * Auth gate for the routes that sell something. A layout route rather than a
 * wrapper above the router, so only what is nested inside it is gated and the
 * cancellation, the withdrawal and the DSA notice can stay public.
 */
function AuthGate() {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading } = useAuthContext();

  if (isLoading) {
    return (
      <div className="login-loading-overlay">
        <div className="login-loading-inner">
          <div className="login-loading-logo">
            <img src="/topbar_logo.svg" alt="Lyra" />
          </div>
          <div className="login-loading-spinner" />
          <p className="login-loading-text">{t("app.loading")}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  return <Outlet />;
}

/** The pricing table, fed with the signed-in user from the auth context. */
function PricingRoute() {
  const { userData } = useAuthContext();

  return <PricingTable userData={userData} isLoadingUser={false} />;
}

function AppContent() {
  return (
    <Routes>
      {/*
        Public: no login, no token, no redirect - § 312k Abs. 2 BGB, § 356a BGB
        and Art. 16 DSA require these to be reachable by anyone at any time.
      */}
      <Route element={<AppLayout />}>
        <Route path={CANCELLATION_PATH} element={<CancellationPage />} />
        <Route
          path={CANCELLATION_CONFIRM_PATH}
          element={<CancellationConfirmPage />}
        />
        <Route path={WITHDRAWAL_PATH} element={<WithdrawalPage />} />
        <Route
          path={WITHDRAWAL_CONFIRM_PATH}
          element={<WithdrawalConfirmPage />}
        />
        <Route path={DSA_PATH} element={<DsaReportPage />} />
      </Route>

      {/* Gated: everything that sells something, including the catch-all. */}
      <Route element={<AuthGate />}>
        <Route element={<AppLayout />}>
          <Route index element={<PricingRoute />} />
          <Route path="order/:pricingId" element={<OrderPage />} />
          <Route
            path="*"
            element={<Navigate to={withEntryQuery("/")} replace />}
          />
        </Route>
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
